import React from 'react';

import { useCameraStreamStart } from 'src/features/camera/api/useCameraStreamStart';
import Video from 'src/features/videoPlayer/Video';
import { useParking } from 'src/providers/ParkingProvider';

type Props = {
    selectedGateway: string;
    loading: boolean;
    setIsPlaying: React.Dispatch<React.SetStateAction<boolean>>;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    setError: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function WebRTCVideoPlayer(props: Props) {
    const { selectedGateway, setIsPlaying, loading, setLoading, setError } = props;
    const { parkingId } = useParking();

    const videoRef = React.useRef<HTMLVideoElement>(null);
    const { mutateAsync: startCameraWebRTC } = useCameraStreamStart({
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            setError(true);
        },
    });

    React.useEffect(() => {
        const playVideo = () => {
            const webrtc = new RTCPeerConnection({
                iceServers: [
                    {
                        urls: [
                            'stun:stun.l.google.com:19302',
                            'stun:stun1.l.google.com:19302',
                            'stun:stun2.l.google.com:19302',
                            'stun:stun3.l.google.com:19302',
                            'stun:stun4.l.google.com:19302',
                        ],
                    },
                ],
                // @ts-expect-error This is a valid option in Chromium https://webrtc.org/getting-started/unified-plan-transition-guide
                sdpSemantics: 'unified-plan',
            });
            webrtc.ontrack = function (event) {
                if (videoRef.current) {
                    videoRef.current.srcObject = event.streams[0];
                    videoRef.current
                        .play()
                        .then(() => {
                            setLoading(false);
                            setIsPlaying(true);
                        })
                        .catch((error) => {
                            console.warn('play error', error);
                        });
                }
            };
            webrtc.addTransceiver('video', { direction: 'sendrecv' });
            const negotiate = async () => {
                const offer = await webrtc.createOffer();
                await webrtc.setLocalDescription(offer);

                const response = await startCameraWebRTC({
                    gatewayId: selectedGateway,
                    parkingId: parkingId,
                    sdp: btoa(offer.sdp || ''),
                });

                await webrtc.setRemoteDescription({
                    type: 'answer',
                    sdp: atob(response.sdp),
                });
            };

            negotiate().catch(() => {
                setLoading(false);
                setError(true);
            });

            const webrtcSendChannel = webrtc.createDataChannel('camera-webrtc-send');
            let webrtcSendChannelInterval: ReturnType<typeof setInterval>;
            webrtcSendChannel.onopen = () => {
                console.log(`${webrtcSendChannel.label} has opened`);
                setIsPlaying(true);
                webrtcSendChannel.send('ping');
                webrtcSendChannelInterval = setInterval(() => {
                    webrtcSendChannel.send('ping');
                }, 30 * 1000);
            };
            webrtcSendChannel.onclose = (_event) => {
                setIsPlaying(false);
                stopVideo();
                playVideo();
            };
            webrtcSendChannel.onmessage = (event) => console.log(event.data);

            function stopVideo() {
                if (webrtcSendChannelInterval) {
                    clearInterval(webrtcSendChannelInterval);
                }

                webrtc.close();
                webrtcSendChannel.close();
                setIsPlaying(false);
                setLoading(true);

                if (videoRef.current) {
                    videoRef.current.pause();
                    videoRef.current.srcObject = null;
                }
            }

            return stopVideo;
        };

        return playVideo();
    }, [selectedGateway, startCameraWebRTC, setIsPlaying, setLoading, setError, parkingId]);

    return <Video loading={loading} videoRef={videoRef} />;
}
